import type { LlmModelSelectDto } from '@@types/api.types';
import { UseChatHelpers } from '@ai-sdk/react';
import { LlmUIMessage as LlmChatMessage } from '@azymmetric/shared/types/llm.types';
import { IconButton } from '@components/core/icon-button.component';
import { LlmModelSelector } from '@components/shared/llm-model-selector.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { Textarea } from '@components/ui/textarea';
import { cn } from '@lib/utils';
import { ChevronUpIcon, MessageSquareTextIcon, Send, Square, XIcon } from 'lucide-react';
import { type Dispatch, type FC, type RefObject, type SetStateAction, useCallback, useRef, useState } from 'react';
import { type LlmChatComment, composeMessageWithComments } from './llm-chat.util';

type LlmChatInputProps = {
  input: string;
  modelFilter?: (model: LlmModelSelectDto) => boolean;
  /**
   * Clear the staged comments once they have been sent with a message.
   */
  onClearComments?: () => void;
  onRemoveComment?: (id: string) => void;
  /**
   * Comments staged from the Suggestion panel; attached to the next message.
   */
  pendingComments?: LlmChatComment[];
  placeholder?: string;
  sendMessage: UseChatHelpers<LlmChatMessage>['sendMessage'];
  setInput: Dispatch<SetStateAction<string>>;
  status: UseChatHelpers<LlmChatMessage>['status'];
  stop: UseChatHelpers<LlmChatMessage>['stop'];
  /**
   * Optional external ref to the textarea (e.g. so the modal can focus the input
   * after staging a comment).
   */
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
};

export const LlmChatInput: FC<LlmChatInputProps> = (props) => {
  const {
    input,
    modelFilter,
    onClearComments,
    onRemoveComment,
    pendingComments = [],
    placeholder = 'Describe what you want, or ask for changes…',
    sendMessage,
    setInput,
    status,
    stop,
    textareaRef: externalTextareaRef,
  } = props;
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = externalTextareaRef ?? internalTextareaRef;
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const isBusy = status === 'streaming' || status === 'submitted';
  const canSubmit = Boolean(input.trim() || pendingComments.length > 0);

  const submitForm = useCallback(() => {
    const text = composeMessageWithComments({ comments: pendingComments, text: input });
    if (!text) return;

    sendMessage({
      role: 'user',
      parts: [
        {
          type: 'text',
          text,
        },
      ],
    });

    setInput('');
    onClearComments?.();
    textareaRef.current?.focus();
  }, [input, onClearComments, pendingComments, sendMessage, setInput, textareaRef]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canSubmit && !isBusy) {
        submitForm();
      }
    }
  };

  return (
    <section
      className="w-full shrink-0 cursor-text rounded-xl border border-gray-200 bg-gray-100 focus-within:border-gray-500"
      onClick={() => textareaRef.current?.focus()}
    >
      {pendingComments.length > 0 && (
        <div className="flex items-center border-b border-gray-200 px-2 py-1.5">
          <Popover
            onOpenChange={setIsCommentsOpen}
            open={isCommentsOpen && pendingComments.length > 0}
          >
            <PopoverTrigger asChild>
              <Badge
                className="cursor-pointer rounded-full hover:bg-gray-50"
                size="sm"
                variant="outline"
              >
                <MessageSquareTextIcon className="size-3.5 text-gray-400" />
                <span>
                  {pendingComments.length === 1 ? '1 suggested edit' : `${pendingComments.length} suggested edits`}
                </span>
                <ChevronUpIcon className="size-3 text-gray-400" />
              </Badge>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-80 p-2"
              side="top"
            >
              <div className="flex max-h-64 flex-col gap-1 overflow-y-auto">
                {pendingComments.map((comment) => (
                  <div
                    className="flex items-start gap-2 rounded-md border border-gray-100 bg-gray-50 px-2 py-1 text-xs"
                    key={comment.id}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate italic text-gray-400">“{comment.quote}”</div>
                      <div className="text-gray-700">{comment.text}</div>
                    </div>
                    <IconButton
                      ariaLabel="Remove comment"
                      icon={<XIcon />}
                      onClick={() => onRemoveComment?.(comment.id)}
                      size="sm"
                      type="button"
                      variant="text"
                    />
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit && !isBusy) {
            submitForm();
          }
        }}
      >
        <Textarea
          className={cn(
            'resize-none border-0 bg-transparent focus:border-0 focus:ring-0',
            'focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0',
            'placeholder:text-gray-400',
          )}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          ref={textareaRef}
          rows={3}
          value={input}
        />

        <div className="flex items-end justify-between p-1">
          <div className="flex items-center gap-1">
            <LlmModelSelector
              modelFilter={modelFilter}
              showCapabilityIcons
            />
          </div>
          {isBusy ? (
            <Button
              onClick={stop}
              size="icon"
              type="button"
              variant="secondary"
            >
              <Square className="size-4" />
            </Button>
          ) : (
            <Button
              disabled={!canSubmit}
              size="icon"
              type="submit"
            >
              <Send className="size-4" />
            </Button>
          )}
        </div>
      </form>
    </section>
  );
};
